'use client'

import { Camera, ArrowRight, Loader2, Check } from 'lucide-react'
import { analytics, trackEvent, type EventProperties } from '@/lib/analytics'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import { useScanHandoff, SCAN_ACCEPT } from '@/hooks/use-scan-handoff'
import { IDENTIFY_CTA, identifyCtaProps } from '@/lib/identify-cta'
import { SCAN_CTA_DESTINATION_ATTR } from '@/lib/scan-cta-marker'

interface InlineScanCtaProps {
  /** cta_location for analytics; also stashed as the upload's entry_source. */
  ctaLocation: string
  heading: string
  tagline: string
  /**
   * cta_text override for surfaces with deliberately-framed copy (the Scotts
   * reframe passes its tool label so historical breakdowns keep resolving).
   * Omit to report the canonical IDENTIFY_CTA.label.
   */
  analyticsText?: string
  /**
   * Where the stashed photo rides to. Defaults to the homepage grass-ID flow;
   * the diagnose picker (V2) passes DIAGNOSE_CTA.href so the photo auto-starts
   * on /diagnose instead.
   */
  destination?: string
  /**
   * "You'll get" deliverable chips (V2). Rendered as a small chip row under the
   * copy at every breakpoint; stacked one-per-line on mobile because the copy
   * column is ~120px there. Omit to render none.
   */
  previewChips?: string[]
  /**
   * Additive analytics properties merged into BOTH cta_surface_shown and
   * cta_clicked on this surface (e.g. cta_variant, symptom_key, slot). Never
   * changes cta_location, so historical breakdowns keep resolving.
   */
  analyticsProps?: EventProperties
  /** Accessible label for the upload button; defaults to the grass-ID phrasing. */
  ariaLabel?: string
}

// Class strings from ContextualToolCTA's glass banner. Only the mobile spacing
// diverges (px-4/gap-3 below sm), buying back ~20px of copy-column width so the
// tagline and chips fit at 360px; sm+ restores the shared banner's px-5/gap-4.
// Every class here is already in the precompiled Tailwind bundle.
const bannerClass =
  'my-8 px-4 sm:px-5 py-[18px] rounded-2xl flex items-center gap-3 sm:gap-4 bg-linear-to-b from-white/70 to-white/55 backdrop-blur-xl backdrop-saturate-150 border border-white/65 [box-shadow:0_12px_40px_rgba(22,163,74,0.10),inset_0_1px_0_rgba(255,255,255,0.85)] hover:[box-shadow:0_20px_60px_rgba(22,163,74,0.14),inset_0_1px_0_rgba(255,255,255,0.85)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 group cursor-pointer'

const iconTileClass =
  'grid place-items-center flex-shrink-0 w-[52px] h-[52px] rounded-[14px] bg-linear-to-b from-white/90 to-green-50/75 border border-white/80 [box-shadow:0_6px_16px_rgba(22,163,74,0.14),inset_0_1px_0_rgba(255,255,255,0.9)]'

const ctaPillClass =
  'inline-flex items-center justify-center gap-2 flex-shrink-0 min-h-[44px] px-4 sm:px-5 rounded-full border border-white/35 text-white text-[15px] font-bold tracking-tight bg-linear-to-r from-green-600/90 to-emerald-600/90 backdrop-blur-sm [box-shadow:0_8px_20px_-6px_rgba(22,163,74,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] group-hover:[box-shadow:0_12px_26px_-6px_rgba(22,163,74,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] transition-shadow'

/**
 * Mid-article grass-ID CTA that opens the photo picker RIGHT HERE on the blog
 * post instead of sending the reader to the homepage to start over. The picked
 * file is stashed (lib/pending-scan-file.ts) and the homepage flow auto-starts
 * the analysis on arrival. Clicking anywhere on the card opens the picker;
 * cancelling the picker leaves the reader exactly where they were.
 *
 * Analytics contract: cta_surface_shown fires once when the card is actually
 * scrolled into view, cta_clicked ONCE per interaction (the button press), both
 * keyed on the same `cta_location` so CTR is computable. Selection outcome is a
 * separate blog_scan_handoff event so aggregate cta_clicked counts stay
 * comparable with the link banner this replaced.
 */
export function InlineScanCta({
  ctaLocation,
  heading,
  tagline,
  analyticsText,
  destination,
  previewChips,
  analyticsProps,
  ariaLabel,
}: InlineScanCtaProps) {
  const { inputRef, handingOff, openPicker, onInputChange } = useScanHandoff(ctaLocation, destination)
  // Denominator for cta_clicked. Without it a placement that renders on 10k
  // posts and one that renders on 50 look identical in the click counts.
  const impressionRef = useImpressionEvent<HTMLButtonElement>(
    ctaLocation,
    { surface: 'inline_scan_cta', ...analyticsProps },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <>
      <button
        ref={impressionRef}
        type="button"
        // Lets a prose CTA elsewhere on the page report that it was competing
        // with an in-place picker to the same destination.
        {...{ [SCAN_CTA_DESTINATION_ATTR]: destination ?? IDENTIFY_CTA.href }}
        onClick={() => {
          trackEvent('cta_clicked', {
            ...identifyCtaProps(ctaLocation, analyticsText ? { text: analyticsText } : undefined),
            ...analyticsProps,
          })
          openPicker()
        }}
        disabled={handingOff}
        className={`${bannerClass} w-full text-left`}
        aria-label={ariaLabel ?? 'Upload a photo of your grass to identify it'}
      >
        <div className={iconTileClass}>
          {handingOff ? (
            <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
          ) : (
            <Camera className="w-6 h-6 text-green-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-green-800 font-editorial font-bold text-base md:text-[17px] tracking-tight leading-snug block">
            {heading}
          </span>
          <span className="text-gray-600 text-xs sm:text-sm leading-normal block">
            {tagline}
          </span>
          {previewChips && previewChips.length > 0 && (
            // Class strings limited to utilities already in the precompiled
            // bundle (lib/generated/tailwind.ts). Chips stack in a column below
            // sm: a wrapped row inside the ~120px mobile copy column breaks each
            // chip across three lines.
            <span className="mt-2 block">
              <span className="flex flex-col items-start sm:flex-row sm:flex-wrap gap-1">
                {previewChips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                  >
                    <Check className="w-3 h-3 flex-shrink-0" />
                    {chip}
                  </span>
                ))}
              </span>
            </span>
          )}
        </div>
        <div className={ctaPillClass}>
          {handingOff ? (
            // The icon tile already carries a spinner, so the pill can go
            // label-less on mobile rather than push the copy column narrower.
            <span className="hidden sm:inline">Starting your ID</span>
          ) : (
            <>
              <span className="sm:hidden">Snap</span>
              <span className="hidden sm:inline">Snap or upload</span>
            </>
          )}
          <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-1 transition-transform" />
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={SCAN_ACCEPT}
        capture="environment"
        className="hidden"
        onChange={onInputChange}
      />
    </>
  )
}
